import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { analyticsAPI, builderAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, Briefcase, Building2, FileText, Globe, MapPin, 
  TrendingUp, AlertCircle, Plus, ArrowUpRight, Phone, 
  Calendar, IndianRupee, CheckCircle2, Clock, Send,
  BarChart3, Target, UserCheck, Layers, Eye, XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BuilderDashboard = () => {
  const { user, builder, hasRole } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState('all');

  const canCreateProject = hasRole(['super_admin', 'builder_admin']);

  useEffect(() => {
    loadDashboard();
  }, [selectedProject]);

  const loadDashboard = async () => {
    try {
      const params = selectedProject !== 'all' ? { project_id: selectedProject } : {};
      const response = await analyticsAPI.getDashboard(params);
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)} L`;
    return `₹${amount.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  const pipeline = stats?.pipeline || {};
  const pipelineTotal = stats?.pipeline_total || 0;

  return (
    <div className="space-y-6" data-testid="builder-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Business Overview</h1>
          <p className="text-muted-foreground mt-1">
            Real-time insights for {builder?.name || 'Your Business'}
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select value={selectedProject} onValueChange={setSelectedProject}>
            <SelectTrigger className="w-[200px]">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Projects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Projects</SelectItem>
              {stats?.projects?.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {canCreateProject && (
            <Button onClick={() => navigate('/builder/projects')} data-testid="btn-add-project">
              <Plus className="h-4 w-4 mr-2" />
              Add Project
            </Button>
          )}
        </div>
      </div>

      {/* Key Business Metrics - Row 1 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Sales Calls */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Phone className="h-8 w-8 text-blue-600" />
              <Badge variant="outline" className="bg-blue-500 text-white border-0">Today</Badge>
            </div>
            <div className="mt-3">
              <p className="text-3xl font-bold text-blue-700">{stats?.calls_today || 0}</p>
              <p className="text-sm text-blue-600">Sales Calls Made</p>
              <p className="text-xs text-blue-500 mt-1">{stats?.calls_this_week || 0} this week</p>
            </div>
          </CardContent>
        </Card>

        {/* Active CPs */}
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <UserCheck className="h-8 w-8 text-emerald-600" />
              <Badge variant="outline" className="bg-emerald-500 text-white border-0">Active</Badge>
            </div>
            <div className="mt-3">
              <p className="text-3xl font-bold text-emerald-700">{stats?.active_cps || 0}/{stats?.total_partners || 0}</p>
              <p className="text-sm text-emerald-600">CPs Working</p>
              <p className="text-xs text-emerald-500 mt-1">{stats?.active_cps_percentage || 0}% active this week</p>
            </div>
          </CardContent>
        </Card>

        {/* Brochures Sent */}
        <Card className="bg-gradient-to-br from-violet-50 to-violet-100 border-violet-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <Send className="h-8 w-8 text-violet-600" />
              <Badge variant="outline" className="bg-violet-500 text-white border-0">Shared</Badge>
            </div>
            <div className="mt-3">
              <p className="text-3xl font-bold text-violet-700">{stats?.brochures_shared_today || 0}</p>
              <p className="text-sm text-violet-600">Brochures Sent Today</p>
              <p className="text-xs text-violet-500 mt-1">{stats?.brochures_shared_week || 0} this week</p>
            </div>
          </CardContent>
        </Card>

        {/* Site Visits */}
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <MapPin className="h-8 w-8 text-amber-600" />
              <Badge variant="outline" className="bg-amber-500 text-white border-0">Visits</Badge>
            </div>
            <div className="mt-3">
              <div className="flex items-baseline gap-2">
                <p className="text-3xl font-bold text-amber-700">{stats?.site_visits_completed || 0}</p>
                <p className="text-lg text-amber-500">/ {stats?.site_visits_scheduled || 0}</p>
              </div>
              <p className="text-sm text-amber-600">Done / Scheduled</p>
              <p className="text-xs text-amber-500 mt-1">{stats?.site_visits_this_week || 0} this week</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Closures & Financials - Row 2 */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Bookings/Closures */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="h-5 w-5 text-green-600" />
              Closures
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Total Bookings</p>
                  <p className="text-2xl font-bold text-green-600">{stats?.total_bookings || 0}</p>
                </div>
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/30 rounded-lg text-center">
                  <p className="text-xl font-bold">{stats?.bookings_this_month || 0}</p>
                  <p className="text-xs text-muted-foreground">This Month</p>
                </div>
                <div className="p-3 bg-muted/30 rounded-lg text-center">
                  <p className="text-xl font-bold">{stats?.tokens_received || 0}</p>
                  <p className="text-xs text-muted-foreground">Tokens</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Commission Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <IndianRupee className="h-5 w-5 text-amber-600" />
              Commissions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">Total Payable</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats?.total_commission_value)}</p>
                </div>
                <IndianRupee className="h-10 w-10 text-amber-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-green-600">{formatCurrency(stats?.paid_commission_value)}</p>
                  <p className="text-xs text-green-600">Paid</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg text-center">
                  <p className="text-lg font-bold text-orange-600">{formatCurrency(stats?.pending_commission_value)}</p>
                  <p className="text-xs text-orange-600">Pending</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inventory Summary */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="h-5 w-5 text-blue-600" />
              Inventory
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Sold Progress</span>
                <span className="text-sm font-medium">{stats?.inventory_sold_percentage || 0}%</span>
              </div>
              <Progress value={stats?.inventory_sold_percentage || 0} className="h-3" />
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-emerald-50 rounded">
                  <p className="text-lg font-bold text-emerald-600">{stats?.available_units || 0}</p>
                  <p className="text-xs text-emerald-600">Available</p>
                </div>
                <div className="p-2 bg-red-50 rounded">
                  <p className="text-lg font-bold text-red-600">{stats?.sold_units || 0}</p>
                  <p className="text-xs text-red-600">Sold</p>
                </div>
                <div className="p-2 bg-amber-50 rounded">
                  <p className="text-lg font-bold text-amber-600">{stats?.blocked_units || 0}</p>
                  <p className="text-xs text-amber-600">Blocked</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales Pipeline */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Sales Pipeline
              </CardTitle>
              <CardDescription>{pipelineTotal} total leads in pipeline</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate('/builder/crm')}>
              View CRM
              <ArrowUpRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {[
              { key: 'new', label: 'New', color: 'bg-blue-500' },
              { key: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
              { key: 'qualified', label: 'Qualified', color: 'bg-purple-500' },
              { key: 'site_visit_scheduled', label: 'SV Scheduled', color: 'bg-indigo-500' },
              { key: 'site_visit_completed', label: 'SV Done', color: 'bg-cyan-500' },
              { key: 'negotiation', label: 'Negotiation', color: 'bg-orange-500' },
              { key: 'booked', label: 'Booked', color: 'bg-green-500' },
              { key: 'lost', label: 'Lost', color: 'bg-red-500' },
            ].map((stage) => (
              <div 
                key={stage.key} 
                className="text-center p-3 rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/builder/crm?status=${stage.key}`)}
              >
                <div className={`w-3 h-3 rounded-full ${stage.color} mx-auto mb-2`}></div>
                <p className="text-2xl font-bold">{pipeline[stage.key] || 0}</p>
                <p className="text-xs text-muted-foreground">{stage.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions & Pending Items */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/builder/partners?status=pending')}
            >
              <Users className="h-4 w-4 mr-3" />
              Review Partner Applications
              {stats?.pending_approvals > 0 && (
                <Badge className="ml-auto bg-amber-500">{stats.pending_approvals}</Badge>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/builder/tagging?status=pending')}
            >
              <FileText className="h-4 w-4 mr-3" />
              Pending Client Taggings
              {stats?.pending_taggings > 0 && (
                <Badge className="ml-auto bg-violet-500">{stats.pending_taggings}</Badge>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/builder/microsites?status=pending_approval')}
            >
              <Globe className="h-4 w-4 mr-3" />
              Approve Microsites
              {stats?.microsites_pending > 0 && (
                <Badge className="ml-auto bg-cyan-500">{stats.microsites_pending}</Badge>
              )}
            </Button>
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={() => navigate('/builder/site-visits')}
            >
              <Calendar className="h-4 w-4 mr-3" />
              Today's Site Visits
              {stats?.site_visits_today > 0 && (
                <Badge className="ml-auto bg-rose-500">{stats.site_visits_today}</Badge>
              )}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Performance Snapshot</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium">Total Leads</p>
                  <p className="text-sm text-muted-foreground">All time</p>
                </div>
              </div>
              <p className="text-2xl font-bold">{stats?.total_leads || 0}</p>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium">New Leads</p>
                  <p className="text-sm text-muted-foreground">Uncontacted</p>
                </div>
              </div>
              <p className="text-2xl font-bold text-emerald-600">{stats?.new_leads || 0}</p>
            </div>
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-violet-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-violet-600" />
                </div>
                <div>
                  <p className="font-medium">Projects</p>
                  <p className="text-sm text-muted-foreground">Active</p>
                </div>
              </div>
              <p className="text-2xl font-bold">{stats?.total_projects || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BuilderDashboard;
